import { rescaleEntriesToWindow } from '../timelinePlacement';
import type {
  DraftEntry,
  LoggerAction,
  LoggerDraft,
} from './loggerDraftTypes';

// The SESSION's own fields — its name, its place, and the window it ran in —
// split out of loggerNestedReducer.ts when LG-77 had to add a case to a file
// that was already 16 lines over the 500-line budget (it had crossed while
// nobody was measuring: the cap is checked on the changeset, and no changeset
// had touched it since).
//
// Same seam and same mechanism as the noteEventReducer / loggerGroupReducer
// delegations already in that file: the parent narrows the union and hands the
// action over, behaviour unchanged.
//
// Why THIS cluster: every case here writes a field of the workout itself and
// touches entries only to keep them consistent with it (the place fills into
// entries that never named one; a corrected window reflows the set times
// inside it). The rest of the parent switch reasons about one entry or one
// set. That line already existed — this only makes it a file boundary.

export type LoggerSessionAction = Extract<
  LoggerAction,
  {
    type:
      | 'set_name'
      | 'set_location'
      | 'set_started_at'
      | 'set_ended_at'
      | 'start_today_draft';
  }
>;

export function loggerSessionReducer(
  state: LoggerDraft,
  action: LoggerSessionAction,
): LoggerDraft {
  switch (action.type) {
    case 'set_name': {
      const trimmed = action.name.slice(0, 120);
      if (trimmed.length === 0) {
        // Empty string -> drop the field entirely so buildPayload's
        // `draft.name ? { name } : {}` check correctly omits it.
        const { name: _drop, ...rest } = state;
        return rest;
      }
      return { ...state, name: trimmed };
    }
    case 'set_location': {
      // Entries that have NOT been told a place yet are filled in; entries
      // already carrying one are left exactly as they are.
      //
      // ⭐ Why filling in is not the same as re-marking. Starting a program
      // creates every entry up front with `location: null`
      // (planStarterHydration), so "only new entries inherit" would tag
      // nothing at all in the commonest flow — you press Start, then say
      // where you are, and by then every entry already exists. `null` means
      // "not said", not "said: nowhere", so writing the answer into it
      // records what was true all along.
      //
      // ⛔ An entry that already names a DIFFERENT place is never touched.
      // That is the genuine change-of-gym, it concerns sets that were really
      // done somewhere else, and it is EQ-1-SWITCH's job to ask — not this
      // reducer's to decide silently.
      //
      // ⭐ A CORRECTION is also filled in, and this is the second half of the
      // rule. Tap the wrong place, notice, tap the right one: without this the
      // first tap would have stamped every then-empty entry with the wrong gym
      // and the second would leave them wrong, with nothing on screen saying
      // so. The test for "was this real?" is not how fast you retapped — it is
      // whether any work was RECORDED there: an entry carrying the previous
      // session place with NO completed set has nothing to describe, so
      // rewriting it loses nothing.
      //
      // ⛔ An entry with a completed set is never touched, whatever it says.
      // Those sets really happened at the place they name, and moving them is
      // EQ-1-SWITCH's question to ask, never this reducer's to answer.
      const previous = state.location ?? null;
      const fill = (entry: DraftEntry): DraftEntry => {
        const current = entry.location ?? null;
        if (current !== null && current !== previous) return entry;
        if (current !== null && entry.sets.some((s) => s.completedAt !== null))
          return entry;
        return { ...entry, location: action.location };
      };
      const entries = state.entries.map(fill);
      if (action.location === null) {
        const { location: _drop, ...rest } = state;
        return { ...rest, entries };
      }
      return { ...state, location: action.location, entries };
    }
    case 'set_started_at': {
      // Closed session (endedAt present): proportionally reflow the
      // existing set times into the new window so correcting the start
      // doesn't strand sets outside [startedAt, endedAt]
      // (next/timeline.md). Live workouts (no endedAt) keep the old
      // behaviour — just move the start clock.
      if (state.endedAt) {
        const s0 = Date.parse(state.startedAt);
        const s1 = Date.parse(action.startedAt);
        const e = Date.parse(state.endedAt);
        if (Number.isFinite(s0) && Number.isFinite(s1) && Number.isFinite(e)) {
          return {
            ...state,
            startedAt: action.startedAt,
            entries: rescaleEntriesToWindow(state.entries, s0, e, s1, e),
          };
        }
      }
      return { ...state, startedAt: action.startedAt };
    }
    case 'start_today_draft': {
      const { todayProposal: _drop, ...rest } = state;
      return { ...rest, startedAt: action.startedAt };
    }
    case 'set_ended_at': {
      if (action.endedAt === null) {
        const { endedAt: _drop, ...rest } = state;
        return rest;
      }
      // Rescale only when a closed window already existed (correcting an
      // end). The first time endedAt is set — finishing a live workout —
      // there's nothing to reflow, so just stamp the value.
      if (state.endedAt) {
        const s = Date.parse(state.startedAt);
        const e0 = Date.parse(state.endedAt);
        const e1 = Date.parse(action.endedAt);
        if (Number.isFinite(s) && Number.isFinite(e0) && Number.isFinite(e1)) {
          return {
            ...state,
            endedAt: action.endedAt,
            entries: rescaleEntriesToWindow(state.entries, s, e0, s, e1),
          };
        }
      }
      return { ...state, endedAt: action.endedAt };
    }
  }
}
